import React, { createContext, useState, ReactElement, FC, useEffect, useRef } from "react";
import { useAuth, useLogout } from "../hooks";
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { toast } from "sonner";
import { WebSocketSubscribeMessageType } from "../types";

type WebSocketContextType = {
  send?: (message: string) => void;
  sendJson?: (message: object) => void;
  subscribe: (callback: (message: WebSocketSubscribeMessageType) => void) => () => void;
  readyState: ReadyState
} | undefined;

type Props = {
  children: ReactElement
}

const WebSocketContext = createContext<WebSocketContextType>(undefined);

export const WebSocketProvider: FC<Props> = ({ children }) => {
  const { webSocketToken } = useAuth();
  const subscribers = useRef<((message:WebSocketSubscribeMessageType) => void)[]>([]);
  const shouldReconnect = useRef(true);
  const logout = useLogout()

  const [url, setUrl] = useState<string | null>(
    webSocketToken?.current ? `${process.env.REACT_APP_BASE_WEBSOCKET_URL}?token=${webSocketToken.current}` : null
  );

  useEffect(() => {
    if (webSocketToken?.current) {
      shouldReconnect.current = true
      setUrl(`${process.env.REACT_APP_BASE_WEBSOCKET_URL}?token=${webSocketToken.current}`);
    }else shouldReconnect.current = false
  }, [webSocketToken.current]);

  // Close WebSocket connection on component unmount by setting URL to null
  useEffect(() => {return () => setUrl(null);}, []);

  const { sendMessage, sendJsonMessage, readyState } = useWebSocket(url, {
    onOpen: () => console.log('WebSocket connection established.'),    
    onMessage: (event) => {
      try {
        const data = JSON.parse(event.data);
        const {type, content} = data;

        subscribers.current.forEach((callback) => callback({...content}));

        if(type === "message"){
          const { type, title, message, shipmentId } = content
          if(title)
            return toast[type as ('info' | 'error' | "success" | "warning")](title, {description:message})
          return toast[type as ('info' | 'error' | "success" | "warning")](message)
        }
        else throw new Error(`The type "${type}" is not recognized in socket`)
      } catch (e) {
        console.error("Receiving websocket message failed.", e, event.data);
      }
    },
    onClose: (v) => {
      console.warn('WebSocket connection closed.', v);
      if(v.code == 1008){
        shouldReconnect.current = false
        logout()
      }
    },
    onError: (event) => {
      console.info('WebSocket error:', event);
    },
    shouldReconnect: () => shouldReconnect.current,
    // Connect only if URL is set
    filter: () => url !== null,
  });

  const subscribe = (callback: (message: WebSocketSubscribeMessageType) => void) => {
    const i = subscribers.current.push(callback) - 1
    // Return an unsubscribe function
    return () => {
      subscribers.current.splice(i, 1);
    };
  };

  const ws = {
    send: sendMessage,
    sendJson: sendJsonMessage,
    readyState,
    subscribe
  };

  return (
    <WebSocketContext.Provider value={ws}>
      {children}
    </WebSocketContext.Provider>
  );
}

export default WebSocketContext;